import { clients } from '@moovly/clients';
import { ContractsResponseWithLicense } from '@moovly/clients/lib/resources/licenses/licenses.interfaces';
import { t } from '@moovly/i18n';
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  DefaultApiRequestState,
  getDefaultApiRequestState,
  getDefaultReducerForAsyncRequest,
  RequestType,
} from '../../../store/default';
import { toastInfo } from '../../../utils/ui';

const CONSENT_ERROR_CODE = 428;

export const getContracts = createAsyncThunk<
  ContractsResponseWithLicense[],
  { externalType: string; externalId: string }
>('dashboard/licenses/getContracts', ({ externalType, externalId }) => {
  if (externalType === 'organization') {
    return clients.Licenses.getOrganizationContracts(externalId);
  }
  return clients.Licenses.getGroupContracts(externalId!);
});
export const grantUserLicense = createAsyncThunk(
  'licenses/grant',
  async ({ userId, licenseId, contractId }: { userId: string; licenseId: string; contractId: string }) => {
    return await clients.Licenses.patchContractLicense(licenseId, userId)
      .then(() => {
        toastInfo(t('alerts.license.assigned'));
        return {
          userId,
          contractId,
          licenseId,
        };
      })
      .catch((error: any) => {
        toastInfo(t('alerts.license.assignedError'));
        throw error;
      });
  }
);
export const revokeLicense = createAsyncThunk(
  'licenses/revoke',
  async ({ licenseId, contractId, silence }: { licenseId: string; contractId: string; silence?: boolean }) => {
    return await clients.Licenses.patchContractLicense(licenseId)
      .then(() => {
        !silence && toastInfo(t('alerts.license.revoked'));
        return {
          contractId,
          licenseId,
        };
      })
      .catch((error: any) => {
        !silence && toastInfo(t('alerts.license.revokedError'));
        throw error;
      });
  }
);
export const orderLicense = createAsyncThunk(
  'licenses/order',
  async (
    {
      contractId,
      amount,
      consentedWarning,
      userId, //pass a userId if directly assigning it.
    }: {
      contractId: string;
      amount: number;
      consentedWarning: boolean;
      userId?: string;
    },
    { dispatch }
  ) => {
    return await clients.Licenses.addContractLicense(contractId, amount, consentedWarning)
      .then((response) => {
        toastInfo(t('alerts.license.order'));
        const license = response[0];
        dispatch(LicenseSlice.actions.successfulPayment());
        if (userId) {
          dispatch(
            grantUserLicense({
              contractId,
              licenseId: license.id,
              userId,
            })
          );
        }
        return { license, contractId, userId };
      })
      .catch((error: any) => {
        if (error.code !== CONSENT_ERROR_CODE) {
          toastInfo(error.message || t('alerts.license.orderError'));
        }
        dispatch(LicenseSlice.actions.errPayment(error));
        throw error;
      });
  }
);

type LicensesState = {
  contracts: DefaultApiRequestState<
    ContractsResponseWithLicense[],
    [RequestType.Get, RequestType.Post, RequestType.Put, RequestType.Delete]
  >;
  payment: {
    loading: boolean;
    warning: boolean;
    success: boolean;
    error: string | null;
  };
};

const initialState: LicensesState = {
  contracts: getDefaultApiRequestState([RequestType.Get, RequestType.Post, RequestType.Put, RequestType.Delete], []),
  payment: {
    loading: false,
    warning: false,
    success: false,
    error: null,
  },
};

export const LicenseSlice = createSlice({
  name: 'subscriptions/licenses',
  initialState,
  reducers: {
    resetPayment: (state) => {
      state.payment = {
        error: null,
        loading: false,
        success: false,
        warning: false,
      };
    },
    errPayment: (state, { payload }: PayloadAction<{ message: string; code: number }>) => {
      state.payment = {
        error: payload.message,
        loading: false,
        success: false,
        warning: payload.code === CONSENT_ERROR_CODE,
      };
    },
    successfulPayment: (state) => {
      state.payment = {
        error: null,
        loading: false,
        success: true,
        warning: false,
      };
    },
  },
  extraReducers: {
    ...getDefaultReducerForAsyncRequest({
      thunk: getContracts,
      requestType: RequestType.Get,
      getInitialState: (state) => state.contracts,
      onSuccess: (state, payload) => {
        state.data = payload;
      },
    }),
    ...getDefaultReducerForAsyncRequest({
      thunk: grantUserLicense,
      requestType: RequestType.Put,
      getInitialState: (state) => state.contracts,
      onSuccess: (state, payload) => {
        const contractIndex = state.data.findIndex((x: any) => x.id === payload.contractId);
        const licenseIndex = state.data[contractIndex].licenses.findIndex((x: any) => x.id === payload.licenseId);
        state.data[contractIndex].licenses[licenseIndex].assigned_to = payload.userId;
        state.data[contractIndex].licenses[licenseIndex].assignments_left -= 1;
      },
    }),
    ...getDefaultReducerForAsyncRequest({
      thunk: revokeLicense,
      requestType: RequestType.Put,
      getInitialState: (state) => state.contracts,
      onSuccess: (state, payload) => {
        const contractIndex = state.data.findIndex((x: any) => x.id === payload.contractId);
        const licenseIndex = state.data[contractIndex].licenses.findIndex((x: any) => x.id === payload.licenseId);
        const license = state.data[contractIndex].licenses[licenseIndex];
        license.assigned_to = null;
        license.assignments_left = Math.min(0, license.assignmentsLeft - 1);
      },
    }),
    ...getDefaultReducerForAsyncRequest({
      thunk: orderLicense,
      requestType: RequestType.Put,
      getInitialState: (state) => state.contracts,
      onSuccess: (state, payload) => {
        if (!!payload) {
          const contractIndex = state.data.findIndex((x: any) => x.id === payload.contractId);
          state.data[contractIndex].licenses.push({
            ...payload.license,
            assigned_to: payload.userId || null,
          });
        }
      },
    }),
  },
});

export default LicenseSlice;
